import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { WhatsAppButton } from './WhatsAppButton';
import { Button } from './ui/button';
import { Target, Users, Lightbulb, Heart, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// Team member photos
const TEAM_PHOTOS = {
  amir: 'https://customer-assets.emergentagent.com/job_cf89158a-bcc4-4183-9507-642ba4ecfd15/artifacts/qbdh4hyl_IMG_6451.JPG',
  emna: 'https://customer-assets.emergentagent.com/job_cf89158a-bcc4-4183-9507-642ba4ecfd15/artifacts/70zq6ns0_Emna%20Sayed.jpg',
  amal: 'https://customer-assets.emergentagent.com/job_cf89158a-bcc4-4183-9507-642ba4ecfd15/artifacts/gx1fp2uc_IMG_6463.JPG',
  souha: 'https://customer-assets.emergentagent.com/job_cf89158a-bcc4-4183-9507-642ba4ecfd15/artifacts/3193pdn6_WhatsApp%20Image%202026-03-16%20at%2014.35.50.jpeg',
};

export const AboutPage = () => {
  const { t, isRTL } = useLanguage();

  const teamMembers = [
    {
      name: 'Amir Belhadj',
      role: t('about.team.ceo'),
      photo: TEAM_PHOTOS.amir,
      description: t('about.team.amirDesc'),
    },
    {
      name: 'Emna Sayed',
      role: t('about.team.projectManager'),
      photo: TEAM_PHOTOS.emna,
      description: t('about.team.emnaDesc'),
    },
    {
      name: 'Amal Kouki',
      role: t('about.team.marketing'),
      photo: TEAM_PHOTOS.amal,
      description: t('about.team.amalDesc'),
    },
    {
      name: 'Malek Kouki',
      role: t('about.team.developer'),
      photo: TEAM_PHOTOS.souha,
      description: t('about.team.malekDesc'),
    },
  ];

  const values = [
    {
      icon: Target,
      title: t('about.values.quality.title'),
      description: t('about.values.quality.desc'),
    },
    {
      icon: Users,
      title: t('about.values.customer.title'),
      description: t('about.values.customer.desc'),
    },
    {
      icon: Lightbulb,
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.desc'),
    },
    {
      icon: Heart,
      title: t('about.values.trust.title'),
      description: t('about.values.trust.desc'),
    },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <Header />
      
      <main className="pt-16 md:pt-20">
        {/* Hero Section */}
        <section className="py-12 sm:py-16 md:py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6">
                {t('about.hero.title')}
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-slate-600 leading-relaxed">
                {t('about.hero.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-12 sm:py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className={`space-y-6 ${isRTL ? 'md:order-2' : ''}`}>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900">
                  {t('about.mission.title')}
                </h2>
                <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                  {t('about.mission.text1')}
                </p>
                <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                  {t('about.mission.text2')}
                </p>
              </div>
              <div className={`${isRTL ? 'md:order-1' : ''}`}>
                <div className="bg-brand/10 rounded-2xl p-8 md:p-12">
                  <div className="space-y-4">
                    <div className="text-5xl md:text-6xl font-bold text-brand">100+</div>
                    <p className="text-slate-700 font-medium">{t('about.mission.stat1')}</p>
                  </div>
                  <div className="mt-8 space-y-4">
                    <div className="text-5xl md:text-6xl font-bold text-brand">5+</div>
                    <p className="text-slate-700 font-medium">{t('about.mission.stat2')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-12 sm:py-16 md:py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('about.values.title')}
              </h2>
              <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
                {t('about.values.subtitle')}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                    data-testid={`value-${index}`}
                  >
                    <div className="w-12 h-12 bg-brand/10 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-brand" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{value.title}</h3>
                    <p className="text-slate-600 text-sm">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-12 sm:py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('about.team.title')}
              </h2>
              <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
                {t('about.team.subtitle')}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {teamMembers.map((member, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-lg transition-shadow group"
                  data-testid={`team-member-${index}`}
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={member.photo}
                      alt={member.name}
                      className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${
                        member.name === 'Amal Kouki' ? '' : 'object-top'
                      }`}
                      style={member.name === 'Amal Kouki' ? { objectPosition: '70% center' } : {}}
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-slate-900">{member.name}</h3>
                    <p className="text-brand font-medium text-sm mb-2">{member.role}</p>
                    <p className="text-slate-600 text-sm">{member.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 md:py-24 bg-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
            <div className="max-w-2xl mx-auto space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                {t('about.cta.title')}
              </h2>
              <p className="text-slate-300 text-base sm:text-lg">
                {t('about.cta.subtitle')}
              </p>
              <Link to="/#contact">
                <Button
                  className="h-12 sm:h-14 px-6 sm:px-8 rounded-full bg-brand hover:bg-brand/90 text-white text-base sm:text-lg font-semibold shadow-lg mt-4"
                  data-testid="about-cta-btn"
                >
                  {t('about.cta.button')}
                  <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default AboutPage;
